/**
 * `/content/settings` — Brand voices and platform connections management.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useBrandVoices,
  useCreateBrandVoice,
  useUpdateBrandVoice,
  useDeleteBrandVoice,
  useConnections,
} from "@/hooks/use-content";

function BrandVoiceSection() {
  const voices = useBrandVoices();
  const create = useCreateBrandVoice();
  const update = useUpdateBrandVoice();
  const remove = useDeleteBrandVoice();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", tone: "", sample_posts: "", custom_instructions: "" });

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.tone) return;
    const samplePosts = form.sample_posts
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    create.mutate(
      {
        name: form.name,
        tone: form.tone,
        sample_posts: samplePosts,
        ...(form.custom_instructions && { custom_instructions: form.custom_instructions }),
        is_default: voices.data?.items.length === 0,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setForm({ name: "", tone: "", sample_posts: "", custom_instructions: "" });
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Brand voices</h2>
        <Button size="sm" variant="outline" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "Add voice"}
        </Button>
      </div>

      {showForm && (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-4">
            <form onSubmit={handleCreate} className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="vname" className="text-xs">Name</Label>
                  <Input id="vname" placeholder="Friendly & Fun" {...field("name")} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="tone" className="text-xs">Tone</Label>
                  <Input id="tone" placeholder="professional, casual, playful…" {...field("tone")} />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="samples" className="text-xs">Sample posts (one per line)</Label>
                <Textarea id="samples" rows={3} placeholder="Check out our latest…" className="resize-none" {...field("sample_posts")} />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="ci" className="text-xs">Custom instructions</Label>
                <Input id="ci" placeholder="Always end with a question…" {...field("custom_instructions")} />
              </div>
              <Button type="submit" size="sm" disabled={!form.name || !form.tone || create.isPending}>
                {create.isPending ? "Saving…" : "Save voice"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {voices.isLoading && <Skeleton className="h-20 rounded-xl" />}

      {voices.data?.items.map((voice) => (
        <Card key={voice.id} className="border-gray-200">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">
                {voice.name}
                {voice.is_default && (
                  <span className="ml-2 text-xs font-normal text-primary">(default)</span>
                )}
              </CardTitle>
              <div className="flex gap-2">
                {!voice.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => update.mutate({ id: voice.id, body: { is_default: true } })}
                    disabled={update.isPending}
                  >
                    Set default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (window.confirm(`Delete "${voice.name}"?`)) remove.mutate(voice.id);
                  }}
                  disabled={remove.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Tone: {voice.tone}
            {voice.sample_posts.length > 0 && ` · ${voice.sample_posts.length} sample posts`}
          </CardContent>
        </Card>
      ))}

      {voices.data?.items.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground">No brand voices configured yet.</p>
      )}
    </div>
  );
}

const PLATFORMS = ["instagram", "facebook", "linkedin", "twitter", "blog"] as const;
const CONNECTION_STATUS_STYLES: Record<string, string> = {
  connected: "text-green-700",
  expired: "text-yellow-700",
  revoked: "text-red-700",
};

function ConnectionsSection() {
  const connections = useConnections();

  const connectedMap = new Map(
    connections.data?.items.map((c) => [c.platform, c]) ?? [],
  );

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold text-gray-900">Platform connections</h2>
      <p className="text-xs text-muted-foreground">
        Token management is handled via your secrets vault. Connection status is shown here.
      </p>

      {connections.isLoading && <Skeleton className="h-32 rounded-xl" />}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {PLATFORMS.map((platform) => {
          const conn = connectedMap.get(platform);
          return (
            <Card key={platform} className="border-gray-200">
              <CardContent className="p-4 flex flex-col gap-1">
                <p className="text-sm font-medium capitalize">{platform}</p>
                {conn ? (
                  <>
                    <p className={`text-xs font-medium ${CONNECTION_STATUS_STYLES[conn.status] ?? ""}`}>
                      {conn.status}
                    </p>
                    {conn.display_name && (
                      <p className="text-xs text-muted-foreground">{conn.display_name}</p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">Not connected</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function ContentSettingsPage() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-8 max-w-2xl">
      <header>
        <button
          type="button"
          onClick={() => navigate("/content")}
          className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1 hover:underline"
        >
          ← Content
        </button>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Content settings</h1>
      </header>

      <BrandVoiceSection />
      <ConnectionsSection />
    </div>
  );
}

export default ContentSettingsPage;
